import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/AdminLayout';
import { QUESTIONARIOS, TIPOS, interpretacaoRastreamento } from '../../lib/questionarios';
import { gerarPdfQuestionario } from '../../lib/pdfGenerator';
import { useQuestionarioAnexo } from '../../hooks/useQuestionarioAnexo';
import { usePDFImport } from '../../hooks/usePDFImport';

const s = {
  header: { marginBottom: '32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  title: { fontFamily: "'Inter', sans-serif", fontSize: '34px', fontWeight: 700, color: 'var(--text)' },
  titleSpan: { fontStyle: 'italic', color: 'var(--accent)' },
  backBtn: { padding: '10px 20px', background: 'transparent', border: '1px solid var(--divider)', borderRadius: 'var(--r-btn)', fontSize: '12px', color: 'var(--text-2)', cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' },
  empty: { padding: '60px 40px', textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--divider)', borderRadius: 'var(--r-card)', color: 'var(--text-2)', fontSize: '14px', boxShadow: 'var(--sh-card)' },
  card: { background: 'var(--surface)', border: '1px solid var(--divider)', borderRadius: 'var(--r-card)', marginBottom: '12px', overflow: 'hidden', boxShadow: 'var(--sh-card)' },
  cardHeader: { padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', gap: '12px', flexWrap: 'wrap' },
  cardTitle: { fontSize: '15px', fontWeight: 600, color: 'var(--text)' },
  cardMeta: { fontSize: '12px', color: 'var(--text-2)', marginTop: '2px' },
  filterRow: { display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' },
  filterBtn: { padding: '7px 16px', border: '1px solid var(--divider)', borderRadius: 'var(--r-btn)', background: 'transparent', fontSize: '12px', color: 'var(--text-2)', cursor: 'pointer', fontFamily: "'Inter', sans-serif" },
  filterBtnActive: { background: 'var(--text)', color: 'var(--bg)', borderColor: 'var(--text)' },
  secTitle: { fontSize: '12px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--accent)' },
  linha: { display: 'flex', justifyContent: 'space-between', gap: '16px', padding: '4px 0' },
  itemLabel: { fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.5 },
};

const corValorEscala = v => (v >= 3 ? '#E53935' : v >= 1 ? '#C8922A' : '#B0A490');

function estruturaVazia(q) {
  if (q.secoes) return q.secoes.reduce((acc, secao) => ({ ...acc, [secao.id]: secao.itens.map(() => null) }), {});
  return q.perguntas.map(() => null);
}

function normalizarValorQuestionario(q, pergunta, valor) {
  if (valor == null || valor === '') return null;
  if (q.modo === 'escala') {
    const n = Number(valor);
    return Number.isInteger(n) && n >= 0 && n <= 4 ? n : null;
  }
  if (q.modo === 'frequencia') {
    const texto = String(valor).trim();
    return q.opcoes?.includes(texto) ? texto : null;
  }
  if (pergunta?.opcoes) {
    const texto = String(valor).trim();
    return pergunta.opcoes.includes(texto) ? texto : null;
  }
  if (pergunta?.tipo === 'escala10') {
    const n = Number(valor);
    return Number.isInteger(n) && n >= 0 && n <= 10 ? n : null;
  }
  return String(valor).trim() || null;
}

function normalizarRespostasIA(tipo, dados) {
  const q = QUESTIONARIOS[tipo];
  const bruto = dados?.respostas || {};

  if (q.secoes) {
    const respostas = estruturaVazia(q);
    q.secoes.forEach(secao => {
      const valores = Array.isArray(bruto?.[secao.id]) ? bruto[secao.id] : [];
      respostas[secao.id] = secao.itens.map((_, idx) => normalizarValorQuestionario(q, null, valores[idx]));
    });
    const total = q.modo === 'escala'
      ? Object.values(respostas).flat().reduce((acc, v) => acc + (typeof v === 'number' ? v : 0), 0)
      : null;
    return { respostas, total };
  }

  const lista = Array.isArray(bruto) ? bruto : [];
  const respostas = q.perguntas.map((pergunta, idx) => normalizarValorQuestionario(q, pergunta, lista[idx]));
  return { respostas, total: null };
}

function temRespostasRegistradas(respostas) {
  if (Array.isArray(respostas)) return respostas.some(v => v != null && v !== '');
  if (respostas && typeof respostas === 'object') {
    return Object.values(respostas).flat().some(v => v != null && v !== '');
  }
  return false;
}

// Detalhe expandido de uma resposta, renderizado conforme o modo do questionário
function DetalheResposta({ reg }) {
  const q = QUESTIONARIOS[reg.tipo];
  if (!q) return null;

  // escala (rastreamento) e frequência (qfa): seções
  if (q.secoes) {
    return (
      <div style={{ borderTop: '1px solid var(--divider)' }}>
        {q.secoes.map(secao => {
          const valores = reg.respostas?.[secao.id] || [];
          const subtotal = q.modo === 'escala' ? valores.reduce((acc, v) => acc + (v || 0), 0) : null;
          return (
            <div key={secao.id} style={{ padding: '14px 24px', borderBottom: '1px solid var(--divider)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={s.secTitle}>{secao.titulo}</span>
                {subtotal != null && (
                  <span style={{ fontSize: '12px', fontWeight: 700, color: subtotal >= 10 ? 'var(--error)' : 'var(--text-2)' }}>{subtotal} pts</span>
                )}
              </div>
              {secao.itens.map((item, idx) => (
                <div key={idx} style={s.linha}>
                  <span style={s.itemLabel}>{item}</span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: q.modo === 'escala' ? corValorEscala(valores[idx] ?? 0) : 'var(--text)', whiteSpace: 'nowrap' }}>
                    {valores[idx] ?? '-'}
                  </span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  // aberto (anamnese) e múltipla escolha (disbiose): perguntas
  const respostas = Array.isArray(reg.respostas) ? reg.respostas : [];
  return (
    <div style={{ borderTop: '1px solid var(--divider)' }}>
      {q.perguntas.map((p, idx) => (
        <div key={idx} style={{ padding: '12px 24px', borderBottom: '1px solid var(--divider)' }}>
          <div style={{ fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.5, marginBottom: '3px' }}>{idx + 1}) {p.label}</div>
          <div style={{ fontSize: '13px', fontWeight: 600, color: respostas[idx] ? 'var(--text)' : 'var(--text-2)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
            {respostas[idx] != null && respostas[idx] !== '' ? String(respostas[idx]) : '— sem resposta —'}
          </div>
        </div>
      ))}
    </div>
  );
}

// Prévia (somente leitura) das perguntas de um questionário, renderizada a
// partir das definições em lib/questionarios — igual ao que a aluna vê.
function PreviaQuestionario({ tipo }) {
  const q = QUESTIONARIOS[tipo];
  if (!q) return null;
  return (
    <div style={{ ...s.card, marginBottom: '24px' }}>
      <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--divider)' }}>
        <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text)' }}>{q.icone} {q.titulo} — prévia das perguntas</div>
        {q.instrucoes && <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '4px', lineHeight: 1.5 }}>{q.instrucoes}</div>}
        {q.legenda && (
          <div style={{ marginTop: '8px' }}>
            {q.legenda.map(l => (
              <div key={l.valor} style={{ fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.6 }}>
                <strong style={{ color: 'var(--accent)' }}>{l.valor}</strong> — {l.texto}
              </div>
            ))}
          </div>
        )}
        {q.modo === 'frequencia' && q.opcoes && (
          <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '8px' }}>
            Opções de resposta: {q.opcoes.map(o => `"${o}"`).join(' · ')} porções por semana
          </div>
        )}
      </div>

      {q.secoes ? (
        q.secoes.map(secao => (
          <div key={secao.id} style={{ padding: '14px 24px', borderBottom: '1px solid var(--divider)' }}>
            <div style={{ ...s.secTitle, marginBottom: '8px' }}>{secao.titulo}</div>
            {secao.itens.map((item, idx) => (
              <div key={idx} style={{ fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.7 }}>• {item}</div>
            ))}
          </div>
        ))
      ) : (
        q.perguntas.map((p, idx) => (
          <div key={idx} style={{ padding: '12px 24px', borderBottom: '1px solid var(--divider)' }}>
            <div style={{ fontSize: '13px', color: 'var(--text)', fontWeight: 500, lineHeight: 1.5 }}>
              {idx + 1}) {p.label} {p.obrigatoria && <span style={{ color: 'var(--error)' }}>*</span>}
            </div>
            {p.obs && <div style={{ fontSize: '11px', color: 'var(--text-2)', marginTop: '2px', lineHeight: 1.5 }}>{p.obs}</div>}
            {p.opcoes && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {p.opcoes.map((o, i) => (
                  <span key={i} style={{ fontSize: '11px', color: 'var(--text-2)', border: '1px solid var(--divider)', borderRadius: '100px', padding: '3px 10px' }}>{o}</span>
                ))}
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default function Questionarios() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { uploadAnexo, abrirAnexo, removerAnexo, uploading } = useQuestionarioAnexo();
  const { importarPDF, loading: lendoPDF } = usePDFImport();
  const [registros, setRegistros] = useState([]);
  const [alunas, setAlunas] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState(searchParams.get('tipo') || 'todos');
  const [filtroAluna, setFiltroAluna] = useState(searchParams.get('aluna') || 'todos');
  const [aberto, setAberto] = useState(null);
  const [previaTipo, setPreviaTipo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAnexo, setShowAnexo] = useState(false);
  const [anexoAluna, setAnexoAluna] = useState('');
  const [anexoTipo, setAnexoTipo] = useState('rastreamento');
  const [anexoFile, setAnexoFile] = useState(null);
  const [anexoErro, setAnexoErro] = useState('');
  const [anexoInfo, setAnexoInfo] = useState('');
  const [enviandoLembrete, setEnviandoLembrete] = useState(null);
  const [lembreteMsg, setLembreteMsg] = useState('');

  const carregar = () => {
    Promise.all([
      supabase.from('questionario_respostas').select('*').order('created_at', { ascending: false }),
      supabase.from('alunas').select('id, name, status').order('name'),
    ]).then(([q, a]) => {
      setRegistros(q.data || []);
      setAlunas(a.data || []);
      setLoading(false);
    });
  };

  useEffect(() => { carregar(); }, []);

  async function salvarAnexo() {
    if (!anexoAluna || !anexoFile || uploading) return;
    setAnexoErro('');
    setAnexoInfo('');
    try {
      const arquivo = await uploadAnexo(anexoAluna, anexoTipo, anexoFile);
      const { error } = await supabase.from('questionario_respostas').insert({
        aluna_id: anexoAluna, tipo: anexoTipo, respostas: {}, arquivo,
      });
      if (error) throw error;
      setShowAnexo(false);
      setAnexoFile(null);
      setAnexoAluna('');
      carregar();
    } catch (e) {
      setAnexoErro('Não foi possível anexar. Tente novamente.');
    }
  }

  async function salvarAnexoComIA() {
    if (!anexoAluna || !anexoFile || uploading || lendoPDF) return;
    setAnexoErro('');
    setAnexoInfo('Lendo o PDF com IA...');
    try {
      const q = QUESTIONARIOS[anexoTipo];
      const dadosIA = await importarPDF(anexoFile, 'questionario', {
        questionario: {
          tipo: anexoTipo,
          titulo: q.titulo,
          modo: q.modo,
          instrucoes: q.instrucoes,
          legenda: q.legenda || null,
          opcoes: q.opcoes || null,
          secoes: q.secoes || null,
          perguntas: q.perguntas || null,
        },
      });
      const { respostas, total } = normalizarRespostasIA(anexoTipo, dadosIA);
      const arquivo = await uploadAnexo(anexoAluna, anexoTipo, anexoFile);
      const { error } = await supabase.from('questionario_respostas').insert({
        aluna_id: anexoAluna,
        tipo: anexoTipo,
        respostas,
        total,
        arquivo,
      });
      if (error) throw error;
      setShowAnexo(false);
      setAnexoFile(null);
      setAnexoAluna('');
      setAnexoInfo('');
      carregar();
    } catch (e) {
      setAnexoInfo('');
      setAnexoErro(e.message?.includes('ANTHROPIC_API_KEY')
        ? 'A chave do Claude não está configurada no ambiente local/Netlify.'
        : 'Não foi possível ler o PDF com IA. Você ainda pode salvar apenas o anexo ou tentar outro PDF.');
    }
  }

  async function excluirRegistro(reg) {
    if (!window.confirm('Excluir este registro de questionário?')) return;
    if (reg.arquivo?.path) await removerAnexo(reg.arquivo.path).catch(() => {});
    await supabase.from('questionario_respostas').delete().eq('id', reg.id);
    carregar();
  }

  const tiposRespondidosDaAluna = alunaId => new Set(
    registros.filter(reg => reg.aluna_id === alunaId && TIPOS.includes(reg.tipo)).map(reg => reg.tipo)
  );

  async function dispararPushLembrete(destinos, texto) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return null;
      const response = await fetch('/.netlify/functions/send-push', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          alunaIds: destinos.map(aluna => aluna.id),
          title: 'Questionários pendentes',
          body: texto,
          url: '/aluna/questionarios',
        }),
      });
      return response.json().catch(() => null);
    } catch {
      return null;
    }
  }

  async function enviarLembrete(destinos) {
    if (!destinos.length || enviandoLembrete) return;
    const individual = destinos.length === 1;
    setEnviandoLembrete(individual ? destinos[0].id : 'todos');
    setLembreteMsg('');

    const texto = `Você ainda tem questionários iniciais pendentes. Responda os ${TIPOS.length} questionários para que a equipe possa personalizar seu acompanhamento.`;
    const rows = destinos.map(aluna => ({
      aluna_id: aluna.id,
      remetente: 'admin',
      texto,
      lida: false,
      tipo: 'recado_home',
    }));
    let { error } = await supabase.from('mensagens').insert(rows);
    if (error && String(error.message || '').toLowerCase().includes('tipo')) {
      ({ error } = await supabase.from('mensagens').insert(rows.map(({ tipo, ...row }) => row)));
    }

    if (error) {
      setLembreteMsg(`Não foi possível enviar o lembrete: ${error.message}`);
    } else {
      const push = await dispararPushLembrete(destinos, texto);
      const pushInfo = push?.sent > 0 ? ` Push enviado para ${push.sent} dispositivo(s).` : '';
      setLembreteMsg(`${individual ? 'Lembrete enviado.' : `Lembrete enviado para ${destinos.length} alunos pendentes.`}${pushInfo}`);
    }
    setEnviandoLembrete(null);
  }

  const nomeAluna = id => alunas.find(a => a.id === id)?.name || 'Aluna';
  const fmtDate = iso => new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const alunasComResposta = [...new Set(registros.map(r => r.aluna_id))];
  const filtrados = registros
    .filter(r => filtroTipo === 'todos' || r.tipo === filtroTipo)
    .filter(r => filtroAluna === 'todos' || r.aluna_id === filtroAluna);
  const arquivoEhPDF = anexoFile && (anexoFile.type === 'application/pdf' || anexoFile.name.toLowerCase().endsWith('.pdf'));
  const podeProcessarIA = Boolean(anexoAluna && anexoFile && arquivoEhPDF && !uploading && !lendoPDF);
  const statusAlunas = alunas.map(aluna => {
    const respondidos = tiposRespondidosDaAluna(aluna.id);
    return {
      ...aluna,
      respondidos,
      faltantes: TIPOS.filter(tipo => !respondidos.has(tipo)),
    };
  });
  const alunasPendentes = statusAlunas.filter(aluna => aluna.faltantes.length > 0 && aluna.status !== 'pausada');

  return (
    <AdminLayout>
      <style>{`
        .questionarios-status-grid { display:grid; gap:10px; }
        .questionarios-status-row {
          display:grid; grid-template-columns:minmax(150px,1fr) 150px minmax(190px,1.3fr) auto;
          gap:14px; align-items:center; padding:14px 16px; border-top:1px solid var(--divider);
        }
        @media(max-width:850px) {
          .questionarios-status-row { grid-template-columns:1fr auto; }
          .questionarios-status-missing { grid-column:1 / -1; }
        }
      `}</style>
      <div style={s.header}>
        <h1 style={s.title}>Questionários <span style={s.titleSpan}>das Alunas</span></h1>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={{ ...s.backBtn, background: 'var(--accent)', color: 'var(--btn-text)', borderColor: 'var(--accent)' }} onClick={() => { setShowAnexo(v => !v); setAnexoErro(''); setAnexoInfo(''); }}>
            📎 Anexar PDF
          </button>
          <button style={s.backBtn} onClick={() => navigate('/admin')}>← Voltar</button>
        </div>
      </div>

      {/* Anexar questionário respondido fora do app */}
      {showAnexo && (
        <div style={{ ...s.card, padding: '20px 24px', marginBottom: '24px' }}>
          <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text)', marginBottom: '4px' }}>Anexar questionário respondido fora do app</div>
          <div style={{ fontSize: '12px', color: 'var(--text-2)', marginBottom: '16px', lineHeight: 1.5 }}>
            Escolha a aluna, o questionário e envie o PDF respondido. A IA lê o arquivo, preenche os campos automaticamente e mantém o PDF original anexado.
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={anexoAluna} onChange={e => setAnexoAluna(e.target.value)}
              style={{ padding: '9px 12px', border: '1.5px solid var(--divider)', borderRadius: 'var(--r-input)', fontSize: '13px', color: 'var(--text)', background: 'var(--surface)', fontFamily: "'Inter', sans-serif" }}>
              <option value="">Escolha a aluna...</option>
              {alunas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select value={anexoTipo} onChange={e => setAnexoTipo(e.target.value)}
              style={{ padding: '9px 12px', border: '1.5px solid var(--divider)', borderRadius: 'var(--r-input)', fontSize: '13px', color: 'var(--text)', background: 'var(--surface)', fontFamily: "'Inter', sans-serif" }}>
              {TIPOS.map(t => <option key={t} value={t}>{QUESTIONARIOS[t].titulo}</option>)}
            </select>
            <input type="file" accept=".pdf,application/pdf,image/*" onChange={e => { setAnexoFile(e.target.files?.[0] || null); setAnexoErro(''); setAnexoInfo(''); }}
              style={{ fontSize: '12px', color: 'var(--text-2)', fontFamily: "'Inter', sans-serif" }} />
            <button onClick={salvarAnexoComIA} disabled={!podeProcessarIA}
              style={{ padding: '9px 20px', background: podeProcessarIA ? 'var(--accent)' : 'var(--divider)', color: 'var(--btn-text)', border: 'none', borderRadius: 'var(--r-btn)', fontSize: '13px', fontWeight: 600, cursor: podeProcessarIA ? 'pointer' : 'not-allowed', fontFamily: "'Inter', sans-serif" }}>
              {lendoPDF ? 'Lendo com IA...' : uploading ? 'Enviando...' : 'Ler PDF com IA e salvar'}
            </button>
            <button onClick={salvarAnexo} disabled={!anexoAluna || !anexoFile || uploading || lendoPDF}
              style={{ padding: '9px 16px', background: 'transparent', color: 'var(--text-2)', border: '1px solid var(--divider)', borderRadius: 'var(--r-btn)', fontSize: '13px', fontWeight: 600, cursor: anexoAluna && anexoFile && !uploading && !lendoPDF ? 'pointer' : 'not-allowed', fontFamily: "'Inter', sans-serif" }}>
              Salvar só anexo
            </button>
          </div>
          {anexoFile && !arquivoEhPDF && (
            <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '10px' }}>
              A leitura com IA está disponível para PDF. Fotos podem ser salvas apenas como anexo.
            </div>
          )}
          {anexoInfo && <div style={{ fontSize: '12px', color: 'var(--accent)', marginTop: '10px' }}>{anexoInfo}</div>}
          {anexoErro && <div style={{ fontSize: '12px', color: 'var(--error)', marginTop: '10px' }}>{anexoErro}</div>}
        </div>
      )}

      <section style={{ ...s.card, marginBottom: '24px' }}>
        <div style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text)' }}>Status dos questionários</div>
            <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '4px' }}>
              {statusAlunas.filter(aluna => aluna.faltantes.length === 0).length} completos · {statusAlunas.filter(aluna => aluna.faltantes.length > 0).length} pendentes
            </div>
          </div>
          <button
            type="button"
            disabled={alunasPendentes.length === 0 || Boolean(enviandoLembrete)}
            onClick={() => enviarLembrete(alunasPendentes)}
            style={{ ...s.filterBtn, background: alunasPendentes.length ? 'var(--accent)' : 'var(--divider)', color: 'var(--btn-text)', borderColor: 'transparent', fontWeight: 700, cursor: alunasPendentes.length ? 'pointer' : 'default' }}
          >
            {enviandoLembrete === 'todos' ? 'Enviando...' : `🔔 Lembrar todos os pendentes (${alunasPendentes.length})`}
          </button>
        </div>
        {lembreteMsg && (
          <div style={{ margin: '0 20px 14px', padding: '10px 12px', borderRadius: '12px', background: 'rgba(137,91,64,.08)', color: 'var(--accent)', fontSize: '12px' }}>
            {lembreteMsg}
          </div>
        )}
        <div className="questionarios-status-grid">
          {statusAlunas.map(aluna => {
            const completo = aluna.faltantes.length === 0;
            return (
              <div className="questionarios-status-row" key={aluna.id}>
                <div>
                  <strong style={{ display: 'block', color: 'var(--text)', fontSize: '14px' }}>{aluna.name}</strong>
                  {aluna.status === 'pausada' && <small style={{ color: 'var(--text-3)' }}>Cadastro pausado</small>}
                </div>
                <span style={{ justifySelf: 'start', padding: '5px 10px', borderRadius: '999px', background: completo ? 'rgba(94,104,64,.12)' : 'rgba(137,91,64,.10)', color: completo ? '#5e6840' : 'var(--accent)', fontSize: '11px', fontWeight: 700 }}>
                  {completo ? '✓ Completo' : `${aluna.respondidos.size} de ${TIPOS.length}`}
                </span>
                <div className="questionarios-status-missing" style={{ color: 'var(--text-2)', fontSize: '11px', lineHeight: 1.5 }}>
                  {completo
                    ? 'Todos os questionários respondidos'
                    : `Faltam: ${aluna.faltantes.map(tipo => QUESTIONARIOS[tipo].titulo.replace('Questionário de ', '')).join(', ')}`}
                </div>
                <button
                  type="button"
                  disabled={completo || aluna.status === 'pausada' || Boolean(enviandoLembrete)}
                  onClick={() => enviarLembrete([aluna])}
                  style={{ ...s.filterBtn, whiteSpace: 'nowrap', opacity: completo || aluna.status === 'pausada' ? .45 : 1, cursor: completo || aluna.status === 'pausada' ? 'default' : 'pointer' }}
                >
                  {enviandoLembrete === aluna.id ? 'Enviando...' : '🔔 Enviar lembrete'}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Filtro por tipo */}
      <div style={s.filterRow}>
        <button style={{ ...s.filterBtn, ...(filtroTipo === 'todos' ? s.filterBtnActive : {}) }} onClick={() => setFiltroTipo('todos')}>Todos</button>
        {TIPOS.map(tipo => (
          <button key={tipo} style={{ ...s.filterBtn, ...(filtroTipo === tipo ? s.filterBtnActive : {}) }} onClick={() => setFiltroTipo(tipo)}>
            {QUESTIONARIOS[tipo].icone} {QUESTIONARIOS[tipo].titulo.replace('Questionário de ', '')}
          </button>
        ))}
      </div>

      {/* Filtro por aluna */}
      {alunasComResposta.length > 1 && (
        <div style={s.filterRow}>
          <button style={{ ...s.filterBtn, ...(filtroAluna === 'todos' ? s.filterBtnActive : {}) }} onClick={() => setFiltroAluna('todos')}>Todas as alunas</button>
          {alunasComResposta.map(id => (
            <button key={id} style={{ ...s.filterBtn, ...(filtroAluna === id ? s.filterBtnActive : {}) }} onClick={() => setFiltroAluna(id)}>{nomeAluna(id)}</button>
          ))}
        </div>
      )}

      {/* Prévia das perguntas */}
      <div style={{ ...s.filterRow, alignItems: 'center' }}>
        <span style={{ fontSize: '12px', color: 'var(--text-2)', fontWeight: 600 }}>👁 Ver perguntas:</span>
        {TIPOS.map(tipo => (
          <button key={tipo}
            style={{ ...s.filterBtn, ...(previaTipo === tipo ? s.filterBtnActive : {}) }}
            onClick={() => setPreviaTipo(previaTipo === tipo ? null : tipo)}>
            {QUESTIONARIOS[tipo].icone} {QUESTIONARIOS[tipo].titulo.replace('Questionário de ', '')}
          </button>
        ))}
      </div>

      {previaTipo && <PreviaQuestionario tipo={previaTipo} />}

      {loading ? (
        <div style={s.empty}>Carregando...</div>
      ) : filtrados.length === 0 ? (
        <div style={s.empty}>Nenhum questionário respondido ainda.</div>
      ) : (
        filtrados.map(reg => {
          const q = QUESTIONARIOS[reg.tipo];
          if (!q) return null;
          const interp = reg.tipo === 'rastreamento' && reg.total != null ? interpretacaoRastreamento(reg.total) : null;
          const expanded = aberto === reg.id;
          const temRespostas = temRespostasRegistradas(reg.respostas);
          return (
            <div key={reg.id} style={s.card}>
              <div style={s.cardHeader} onClick={() => setAberto(expanded ? null : reg.id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
                  <span style={{ fontSize: '22px' }}>{q.icone}</span>
                  <div>
                    <div style={s.cardTitle}>{nomeAluna(reg.aluna_id)}</div>
                    <div style={s.cardMeta}>{q.titulo} · {fmtDate(reg.created_at)}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {interp && (
                    <span style={{ padding: '4px 12px', borderRadius: '100px', fontSize: '11px', fontWeight: 700, color: interp.color, background: interp.bg, whiteSpace: 'nowrap' }}>
                      {reg.total} pts · {interp.label}
                    </span>
                  )}
                  {reg.arquivo ? (
                    <button onClick={e => { e.stopPropagation(); abrirAnexo(reg.arquivo.path).catch(() => alert('Não foi possível abrir o arquivo.')); }}
                      style={{ ...s.filterBtn, whiteSpace: 'nowrap' }}>
                      📎 Abrir PDF
                    </button>
                  ) : (
                    <button onClick={e => { e.stopPropagation(); gerarPdfQuestionario(reg, nomeAluna(reg.aluna_id)); }}
                      style={{ ...s.filterBtn, whiteSpace: 'nowrap' }}>
                      ⬇ Salvar PDF
                    </button>
                  )}
                  <button onClick={e => { e.stopPropagation(); excluirRegistro(reg); }}
                    style={{ ...s.filterBtn, padding: '7px 10px' }} title="Excluir">🗑</button>
                  <span style={{ fontSize: '12px', color: 'var(--text-2)' }}>{expanded ? '▲' : '▼'}</span>
                </div>
              </div>
              {expanded && (reg.arquivo && !temRespostas ? (
                <div style={{ borderTop: '1px solid var(--divider)', padding: '16px 24px', fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.6 }}>
                  Questionário respondido fora do app — arquivo anexado pelo admin
                  {reg.arquivo.nomeArquivo ? `: ${reg.arquivo.nomeArquivo}` : ''}. Use o botão "📎 Abrir PDF" para visualizar.
                </div>
              ) : (
                <>
                  {reg.arquivo && (
                    <div style={{ borderTop: '1px solid var(--divider)', padding: '12px 24px', fontSize: '12px', color: 'var(--text-2)', lineHeight: 1.5, background: 'rgba(137,91,64,0.04)' }}>
                      Respostas preenchidas automaticamente a partir do PDF anexado
                      {reg.arquivo.nomeArquivo ? `: ${reg.arquivo.nomeArquivo}` : ''}. Revise os campos antes de usar em conduta.
                    </div>
                  )}
                  <DetalheResposta reg={reg} />
                </>
              ))}
            </div>
          );
        })
      )}
    </AdminLayout>
  );
}
